import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import DatasetSelector from '../Common/DatasetSelector';
import DiscretizationLayout from '../Common/DiscretizationLayout';
import { Box, Paper, Typography, Button, TextField, MenuItem, Select as MuiSelect, FormControl, InputLabel } from '@mui/material';
import '../Discretization/ExpertCut/ExpertCut.css';
import '../RiskDiff/Sampling/Sampling.css';

const SeveritySampling = () => {
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetLoaded, setDatasetLoaded] = useState(false);
  const [showSaveSection, setShowSaveSection] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showTitleEditor, setShowTitleEditor] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState('');
  const plotRefs = useRef({});
  
  const [plotTitle, setPlotTitle] = useState('Sampling Analysis');
  const [xAxisTitle, setXAxisTitle] = useState('');
  const [yAxisTitle, setYAxisTitle] = useState('%');
  const [y2AxisTitle, setY2AxisTitle] = useState('Avg');
  
  const [formData, setFormData] = useState({
    Default_Date: null, 
    Target_variable: null, 
    proportion: 10, 
    method: 'random',
    Risk_drivers: [], 
    indicator_name: ''
  });

  // ========== ADDED: Analysis state variables ==========
  const [savedAnalysisId, setSavedAnalysisId] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState('draft');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(true);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [allDatasets, setAllDatasets] = useState([]);

  // ========== ADDED: Mode detection ==========
  const mode = new URLSearchParams(window.location.search).get('mode');
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit' || !mode;

  const columnOptions = columns.map(c => ({ value: c, label: c }));

  useEffect(() => { 
    if (!window.Plotly) { 
      const script = document.createElement('script'); 
      script.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js'; 
      script.onload = () => console.log('✓ Plotly loaded'); 
      document.head.appendChild(script); 
    } 
  }, []);

  // ========== ADDED: Load saved analysis on mount ==========
  useEffect(() => {
    const analysisId = new URLSearchParams(window.location.search).get('analysis_id');
    if (analysisId && !isNaN(parseInt(analysisId))) {
      loadSavedAnalysis(parseInt(analysisId));
    }
  }, []);

  const fetchDatasets = async () => {
    try {
      const projectId = new URLSearchParams(window.location.search).get('project_id');
      if (!projectId) {
        setAllDatasets([]);
        return;
      }
      const res = await api.get(`/api/v1/tables/project/${projectId}/datasets`);
      const datasets = res.data.map(ds => ({
        id: ds.id,
        name: ds.original_filename,
        rows: ds.row_count,
        columns: ds.column_count
      }));
      setAllDatasets(datasets);
    } catch (err) {
      console.error('Failed to load datasets', err);
    }
  };
  useEffect(() => {
    fetchDatasets();
  }, []);

  // ========== ADDED: Load saved analysis function ==========
  const loadSavedAnalysis = async (analysisId) => {
    setIsLoadingAnalysis(true);
    try {
      const res = await api.get(`/analyses/${analysisId}`);
      const analysis = res.data;
      
      if (analysis.status) setAnalysisStatus(analysis.status);
      if (analysis.description) setAnalysisDescription(analysis.description);
      setSavedAnalysisId(analysis.id);
      if (analysis.comments && Array.isArray(analysis.comments)) setComments(analysis.comments);
      
      if (analysis.input_parameters) {
        if (analysis.input_parameters.dataset_id) {
          setDatasetInfo({
            id: analysis.input_parameters.dataset_id,
            name: analysis.input_parameters.dataset_name,
            rows: analysis.input_parameters.rows,
            columns: analysis.input_parameters.columns
          });
          setDatasetLoaded(true);
          setColumns(analysis.input_parameters.columns || []);
        }
        
        setFormData(prev => ({
          ...prev,
          Default_Date: analysis.input_parameters.Default_Date ? { value: analysis.input_parameters.Default_Date, label: analysis.input_parameters.Default_Date } : null,
          Target_variable: analysis.input_parameters.Target_variable ? { value: analysis.input_parameters.Target_variable, label: analysis.input_parameters.Target_variable } : null,
          proportion: analysis.input_parameters.proportion || 10,
          method: analysis.input_parameters.method || 'random',
          Risk_drivers: (analysis.input_parameters.Risk_drivers || []).map(d => ({ value: d, label: d })),
          indicator_name: analysis.input_parameters.indicator_name || ''
        }));
        
        if (analysis.input_parameters.results) setResults(analysis.input_parameters.results);
        if (analysis.input_parameters.plotTitle) setPlotTitle(analysis.input_parameters.plotTitle);
        if (analysis.input_parameters.xAxisTitle) setXAxisTitle(analysis.input_parameters.xAxisTitle);
        if (analysis.input_parameters.yAxisTitle) setYAxisTitle(analysis.input_parameters.yAxisTitle);
        if (analysis.input_parameters.y2AxisTitle) setY2AxisTitle(analysis.input_parameters.y2AxisTitle);
        if (analysis.input_parameters.selectedDriver) setSelectedDriver(analysis.input_parameters.selectedDriver);
      }
      
      if (analysis.output_result) {
        setStep(2);
        toast.success('Analysis loaded with results');
      } else if (analysis.input_parameters) {
        setStep(1);
        toast.success('Analysis loaded. Continue to run analysis steps.');
      }
    } catch (err) {
      toast.error('Failed to load analysis');
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  // ========== ADDED: Save Analysis function ==========
  const handleSaveAnalysis = async () => {
    if (!datasetInfo) {
      toast.error('No dataset selected');
      return;
    }
    const projectId = new URLSearchParams(window.location.search).get('project_id');
    if (!projectId) {
      toast.error('No project selected');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        project_id: parseInt(projectId),
        module_name: 'severity_sampling',
        description: analysisDescription,
        input_parameters: {
          dataset_id: datasetInfo.id,
          dataset_name: datasetInfo.name,
          rows: datasetInfo.rows,
          columns: datasetInfo.columns,
          Default_Date: formData.Default_Date?.value || '',
          Target_variable: formData.Target_variable?.value || '',
          proportion: formData.proportion,
          method: formData.method,
          Risk_drivers: formData.Risk_drivers.map(d => d.value),
          indicator_name: formData.indicator_name,
          results: results,
          plotTitle: plotTitle,
          xAxisTitle: xAxisTitle,
          yAxisTitle: yAxisTitle,
          y2AxisTitle: y2AxisTitle,
          selectedDriver: selectedDriver
        },
        output_result: results ? {
          outlier_count: results.outlier_count,
          outlier_pct: results.outlier_pct,
          drivers: results.drivers,
          results: results.results
        } : null,
        status: analysisStatus,
        comments: comments
      };
      const res = await api.post('/analyses/', payload);
      toast.success(`Analysis saved with ID: ${res.data.id}`);
      setSavedAnalysisId(res.data.id);
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save analysis');
    } finally {
      setLoading(false);
    }
  };

  // ========== ADDED: Update Metadata function ==========
  const handleUpdateMetadata = async () => {
    if (!savedAnalysisId) {
      toast.error('No analysis saved yet');
      return;
    }
    setUpdatingMetadata(true);
    try {
      await api.patch(`/analyses/${savedAnalysisId}`, {
        status: analysisStatus,
        description: analysisDescription
      });
      toast.success('Status and description updated');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdatingMetadata(false);
    }
  };

  // ========== ADDED: Add Comment function ==========
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    const commentObj = {
      id: Date.now(),
      text: newComment,
      created_at: new Date().toISOString(),
      user_id: localStorage.getItem('user_id') || 'user',
      user_email: localStorage.getItem('user_email') || 'Unknown'
    };
    const updatedComments = [...comments, commentObj];
    
    if (savedAnalysisId) {
      setSavingComment(true);
      try {
        await api.patch(`/analyses/${savedAnalysisId}`, { comments: updatedComments });
        setComments(updatedComments);
        setNewComment('');
        toast.success('Comment added');
      } catch (err) {
        toast.error('Failed to add comment');
      } finally {
        setSavingComment(false);
      }
    } else {
      setComments(updatedComments);
      setNewComment('');
      toast.success('Comment added (will be saved when you save the analysis)');
    }
  };

  const renderPlot = (driver, res, customTitles = null) => {
    if (!res || !plotRefs.current[driver] || !window.Plotly) return;
    
    const titles = customTitles || { plotTitle, xAxisTitle, yAxisTitle, y2AxisTitle };
    
    const trace1 = { 
      x: res.x, y: res.training_pct, type: 'bar', name: 'Train %', 
      marker: { color: '#3498db', opacity: 0.8 }, width: 0.35
    };
    const trace2 = { 
      x: res.x, y: res.testing_pct, type: 'bar', name: 'Test %', 
      marker: { color: '#e74c3c', opacity: 0.8 }, width: 0.35
    };
    const trace3 = { 
      x: res.x, y: res.training_avg, type: 'scatter', mode: 'lines+markers', 
      name: 'Train Avg', line: { color: '#2980b9', width: 2 }, yaxis: 'y2',
      marker: { size: 5 }
    };
    const trace4 = { 
      x: res.x, y: res.testing_avg, type: 'scatter', mode: 'lines+markers', 
      name: 'Test Avg', line: { color: '#c0392b', width: 2 }, yaxis: 'y2',
      marker: { size: 5 }
    };
    
    const layout = {
      title: { text: titles.plotTitle.replace('{driver}', driver), font: { size: 14 } },
      xaxis: { title: titles.xAxisTitle || driver, tickangle: -30, tickfont: { size: 10 }, titlefont: { size: 12 } },
      yaxis: { title: titles.yAxisTitle, side: 'left', titlefont: { size: 12, color: '#3498db' }, tickfont: { size: 10 } },
      yaxis2: { title: titles.y2AxisTitle, overlaying: 'y', side: 'right', titlefont: { size: 12, color: '#e74c3c' }, tickfont: { size: 10 } },
      barmode: 'group', bargap: 0.3, bargroupgap: 0.1,
      template: 'plotly_white', height: 350,
      margin: { t: 40, b: 70, l: 60, r: 60 },
      legend: { orientation: 'h', y: -0.4, x: 0.5, xanchor: 'center', font: { size: 10 } },
      hovermode: 'x unified'
    };
    
    window.Plotly.newPlot(plotRefs.current[driver], [trace1, trace2, trace3, trace4], layout);
  };
  
  useEffect(() => {
    if (results?.results) {
      Object.entries(results.results).forEach(([driver, res]) => {
        if (plotRefs.current[driver]) setTimeout(() => renderPlot(driver, res), 100);
      });
      if (results.drivers?.length > 0) {
        setSelectedDriver(results.drivers[0]);
        setXAxisTitle(results.drivers[0]);
      }
    }
  }, [results]);

  const handleApplyTitles = () => {
    if (results?.results) {
      Object.entries(results.results).forEach(([driver, res]) => {
        if (plotRefs.current[driver]) renderPlot(driver, res, { plotTitle, xAxisTitle, yAxisTitle, y2AxisTitle });
      });
    }
    toast.success('Updated!');
  };

  const handleResetTitles = () => {
    const defaultTitle = 'Sampling: {driver}';
    const defaultX = selectedDriver || '';
    const defaultY = '%';
    const defaultY2 = 'Avg';
    setPlotTitle(defaultTitle); setXAxisTitle(defaultX); setYAxisTitle(defaultY); setY2AxisTitle(defaultY2);
    if (results?.results) {
      Object.entries(results.results).forEach(([driver, res]) => {
        if (plotRefs.current[driver]) renderPlot(driver, res, { plotTitle: defaultTitle, xAxisTitle: driver, yAxisTitle: defaultY, y2AxisTitle: defaultY2 });
      });
    }
    toast.success('Reset!');
  };

  const handleDatasetLoaded = (data) => {
    setColumns(data.columns || []);
    setDatasetInfo({
      id: data.id,
      name: `Dataset ${data.id}`,
      rows: data.rows,
      columns: data.columns
    });
    setDatasetLoaded(true);
    toast.success(`Loaded ${data.rows?.toLocaleString()} rows`);
  };

  // Update name when allDatasets is loaded
  useEffect(() => {
    if (datasetInfo && allDatasets.length > 0) {
      const datasetFromList = allDatasets.find(d => d.id === datasetInfo.id);
      if (datasetFromList && datasetFromList.name !== datasetInfo.name) {
        setDatasetInfo(prev => ({ ...prev, name: datasetFromList.name }));
      }
    }
  }, [allDatasets, datasetInfo]);
  
  const handleFormChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  
  const handleSaveStep0 = async () => { 
    if (!formData.Target_variable) { toast.error('Target required'); return; } 
    setLoading(true); 
    try { 
      const payload = { ...formData, Default_Date: formData.Default_Date?.value || '', Target_variable: formData.Target_variable?.value || '', Risk_drivers: formData.Risk_drivers.map(d => d.value) };
      await api.post('/api/severity/sampling/step0', payload); 
      toast.success('Saved'); setStep(1); 
    } catch { toast.error('Failed'); } 
    finally { setLoading(false); } 
  };
  
  const handleRunAnalysis = async () => { 
    setLoading(true); 
    try { 
      const payload = { ...formData, Default_Date: formData.Default_Date?.value || '', Target_variable: formData.Target_variable?.value || '', Risk_drivers: formData.Risk_drivers.map(d => d.value) };
      const res = await api.post('/api/severity/sampling/step1', payload); 
      setResults(res.data.results); setPlotTitle('Sampling: {driver}');
      toast.success(res.data.message); setStep(2); 
    } catch { toast.error('Failed'); } 
    finally { setLoading(false); } 
  };
  
  const handleSaveColumn = async () => { 
    const colName = formData.indicator_name || 'Sampling_indicator'; 
    setLoading(true); 
    try { 
      const res = await api.post('/api/severity/sampling/step2', { new_column_name: colName }); 
      toast.success(res.data.message); setShowSaveSection(false); 
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); } 
    finally { setLoading(false); } 
  };
  
  const handleExport = async () => { 
    try { 
      const r = await api.get('/api/severity/sampling/export', { responseType: 'blob' }); 
      const u = window.URL.createObjectURL(new Blob([r.data])); 
      const a = document.createElement('a'); 
      a.href = u; a.download = 'sampling_export.xlsx'; a.click(); 
      window.URL.revokeObjectURL(u); toast.success('Exported!'); 
    } catch { toast.error('Failed'); } 
  };
  
  const handleStepClick = (t) => { 
    if (t === 0) setStep(0); 
    else if (t === 1 && datasetLoaded) setStep(1); 
    else if (t === 2 && results) setStep(2); 
  };

  const compactStyles = {
    control: (base) => ({ ...base, minHeight: '32px', height: '32px', borderColor: '#e0e0e0', boxShadow: 'none', fontSize: '12px', '&:hover': { borderColor: '#1abc9c' } }),
    valueContainer: (base) => ({ ...base, padding: '0 6px' }),
    placeholder: (base) => ({ ...base, color: '#95a5a6', fontSize: '12px' }),
    option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? '#1abc9c' : state.isFocused ? '#e8f8f5' : 'white', color: state.isSelected ? 'white' : '#2c3e50', fontSize: '12px', padding: '6px 10px' }),
    multiValue: (base) => ({ ...base, backgroundColor: '#e8f8f5', borderRadius: '3px' }),
    multiValueLabel: (base) => ({ ...base, fontSize: '11px', padding: '1px 4px' }),
    menuList: (base) => ({ ...base, maxHeight: '180px' })
  };

  return (
    <DiscretizationLayout title="Sampling" step={step} onStepClick={handleStepClick} datasetLoaded={datasetLoaded} results={results}>
      {/* ========== ADDED: View mode banner ========== */}
      {isViewMode && results && (
        <div style={{ background: '#e8f8f5', padding: '8px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#1abc9c' }}>👁️ Viewing saved analysis (read-only)</span>
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set('mode', 'edit');
              window.location.href = url.toString();
            }}
            style={{ padding: '4px 12px', fontSize: '12px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Modify
          </button>
        </div>
      )}

      {step === 0 && (
        <>
          <DatasetSelector projectId={new URLSearchParams(window.location.search).get("project_id")} apiPrefix="/api/severity/sampling" onDatasetLoaded={handleDatasetLoaded} />
          
          {/* Show dataset name when a dataset is loaded */}
          {datasetInfo && (
            <div style={{ 
              marginTop: '16px', 
              marginBottom: '16px', 
              padding: '12px', 
              background: '#e8f8f5', 
              borderRadius: '8px',
              borderLeft: '4px solid #1abc9c'
            }}>
              <div style={{ fontSize: '13px', color: '#2c3e50', fontWeight: 500 }}>
                📌 Currently selected dataset:
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#1abc9c', marginTop: '4px' }}>
                {datasetInfo.name}
              </div>
              <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                {datasetInfo.rows?.toLocaleString()} rows • {datasetInfo.columns?.length || 0} columns
              </div>
            </div>
          )}

          {columns.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#7f8c8d', marginBottom: '4px', display: 'block' }}>Claims Date</label>
                  <Select options={columnOptions} value={formData.Default_Date} onChange={(val) => handleFormChange('Default_Date', val)} isClearable isSearchable placeholder="Select..." styles={compactStyles} isDisabled={isViewMode} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#e74c3c', marginBottom: '4px', display: 'block' }}>Target *</label>
                  <Select options={columnOptions} value={formData.Target_variable} onChange={(val) => handleFormChange('Target_variable', val)} isSearchable placeholder="Select..." styles={compactStyles} isDisabled={isViewMode} />
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#7f8c8d', marginBottom: '4px', display: 'block' }}>Testing %</label>
                  <input type="number" value={formData.proportion} onChange={(e) => handleFormChange('proportion', parseFloat(e.target.value))} min="1" max="50" step="1" style={{ width: '100%', padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '12px', height: '32px' }} disabled={isViewMode} />
                </div>
                <button type="button" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} style={{ width: '32px', height: '32px', borderRadius: '4px', background: showAdvancedOptions ? '#1abc9c' : '#f8f9fa', color: showAdvancedOptions ? 'white' : '#7f8c8d', border: '1px solid #e0e0e0', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⚙️</button>
              </div>
              
              {showAdvancedOptions && (
                <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#7f8c8d', marginBottom: '4px', display: 'block' }}>Method</label>
                    <select value={formData.method} onChange={(e) => handleFormChange('method', e.target.value)} style={{ width: '100%', padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '12px', height: '32px', background: 'white' }} disabled={isViewMode}>
                      <option value="random">Simple Random</option>
                      <option value="stratified">Stratified</option>
                    </select>
                  </div>
                  {formData.method === 'stratified' && (
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, color: '#7f8c8d', marginBottom: '4px', display: 'block' }}>Stratification Drivers</label>
                      <Select options={columnOptions.filter(c => c.value !== formData.Target_variable?.value)} value={formData.Risk_drivers} onChange={(val) => handleFormChange('Risk_drivers', val || [])} isMulti isSearchable placeholder="Select drivers..." styles={{ ...compactStyles, control: (base) => ({ ...compactStyles.control(base), minHeight: 'auto', height: 'auto' }) }} closeMenuOnSelect={false} isDisabled={isViewMode} />
                    </div>
                  )}
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={handleSaveStep0} disabled={loading || isViewMode} style={{ padding: '8px 24px', fontSize: '13px', background: loading || isViewMode ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                  {loading ? 'Saving...' : 'Continue →'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
      
      {step === 1 && (
        <div style={{ padding: '8px 0' }}>
          <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '12px' }}>
            <p style={{ margin: '4px 0' }}><strong>Target:</strong> {formData.Target_variable?.label}</p>
            <p style={{ margin: '4px 0' }}><strong>Test %:</strong> {formData.proportion}%</p>
            <p style={{ margin: '4px 0' }}><strong>Method:</strong> {formData.method === 'random' ? 'Simple Random' : 'Stratified'}</p>
            {formData.method === 'stratified' && formData.Risk_drivers.length > 0 && <p style={{ margin: '4px 0' }}><strong>Drivers:</strong> {formData.Risk_drivers.map(d => d.label).join(', ')}</p>}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setStep(0)} style={{ padding: '8px 20px', fontSize: '12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={handleRunAnalysis} disabled={loading || isViewMode} style={{ padding: '8px 24px', fontSize: '12px', background: loading || isViewMode ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{loading ? 'Running...' : '🚀 Run'}</button>
          </div>
        </div>
      )}
      
      {step === 2 && results && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '16px' }}>
            <div style={{ background: 'white', padding: '12px', borderRadius: '6px', textAlign: 'center', border: '1px solid #e0e0e0' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#e74c3c' }}>{results.outlier_count?.toLocaleString()}</div>
              <div style={{ fontSize: '11px', color: '#7f8c8d' }}>Testing</div>
            </div>
            <div style={{ background: 'white', padding: '12px', borderRadius: '6px', textAlign: 'center', border: '1px solid #e0e0e0' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#1abc9c' }}>{results.outlier_pct}%</div>
              <div style={{ fontSize: '11px', color: '#7f8c8d' }}>Test %</div>
            </div>
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <button onClick={() => setShowTitleEditor(!showTitleEditor)} style={{ padding: '4px 10px', fontSize: '11px', background: 'none', border: '1px solid #e0e0e0', borderRadius: '4px', cursor: 'pointer', marginBottom: showTitleEditor ? '8px' : '0' }}>{showTitleEditor ? '✕' : '✏️ Titles'}</button>
            {showTitleEditor && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '10px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '10px' }}>
                <input type="text" value={plotTitle} onChange={(e) => setPlotTitle(e.target.value)} placeholder="Title" style={{ padding: '6px', fontSize: '11px', gridColumn: 'span 2' }} />
                <input type="text" value={xAxisTitle} onChange={(e) => setXAxisTitle(e.target.value)} placeholder="X Axis" style={{ padding: '6px', fontSize: '11px' }} />
                <input type="text" value={yAxisTitle} onChange={(e) => setYAxisTitle(e.target.value)} placeholder="Y Axis" style={{ padding: '6px', fontSize: '11px' }} />
                <input type="text" value={y2AxisTitle} onChange={(e) => setY2AxisTitle(e.target.value)} placeholder="Y2 Axis" style={{ padding: '6px', fontSize: '11px', gridColumn: 'span 2' }} />
                <button onClick={handleApplyTitles} style={{ padding: '6px', fontSize: '11px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', gridColumn: 'span 2' }}>Apply</button>
              </div>
            )}
          </div>
          
          {results.drivers?.length > 0 && (
            <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)} style={{ width: '100%', padding: '6px', fontSize: '12px', borderRadius: '4px', border: '1px solid #e0e0e0', marginBottom: '12px' }}>
              {results.drivers.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          
          {results.results && Object.entries(results.results).map(([driver, res]) => (
            <div key={driver} style={{ display: selectedDriver === driver ? 'block' : 'none' }}>
              <div ref={el => plotRefs.current[driver] = el} style={{ width: '100%', height: '350px' }}></div>
              
              <div className="table-scroll" style={{ marginTop: '12px', maxHeight: '220px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr style={{ background: '#34495e', color: 'white' }}>
                      <th style={{ padding: '8px 6px', textAlign: 'left' }}>{driver}</th>
                      <th style={{ padding: '8px 6px', textAlign: 'left' }}>Sample</th>
                      <th style={{ padding: '8px 6px', textAlign: 'right' }}>Count</th>
                      <th style={{ padding: '8px 6px', textAlign: 'right' }}>%</th>
                      <th style={{ padding: '8px 6px', textAlign: 'right' }}>Avg Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.records?.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #e0e0e0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                        <td style={{ padding: '6px' }}>{r[driver]}</td>
                        <td style={{ padding: '6px' }}>{r.Sample}</td>
                        <td style={{ padding: '6px', textAlign: 'right' }}>{r.Count?.toLocaleString()}</td>
                        <td style={{ padding: '6px', textAlign: 'right' }}>{r.Pct}%</td>
                        <td style={{ padding: '6px', textAlign: 'right' }}>{r.Avg_Target?.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          
          <div style={{ marginTop: '16px' }}>
            <button onClick={() => setShowSaveSection(!showSaveSection)} style={{ padding: '6px 12px', fontSize: '12px', background: 'none', border: '1px solid #e0e0e0', borderRadius: '4px', cursor: 'pointer', marginBottom: showSaveSection ? '10px' : '0' }}>
              {showSaveSection ? '✕ Cancel' : '💾 Save Indicator'}
            </button>
            {showSaveSection && (
              <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '12px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input type="text" value={formData.indicator_name || 'Sampling_indicator'} onChange={(e) => handleFormChange('indicator_name', e.target.value)} disabled={loading || isViewMode} placeholder="Column name" style={{ flex: 1, padding: '8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #e0e0e0' }} />
                <button onClick={handleSaveColumn} disabled={loading || isViewMode} style={{ padding: '8px 16px', fontSize: '12px', background: loading || isViewMode ? '#95a5a6' : '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{loading ? 'Saving...' : 'Save'}</button>
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button onClick={() => setStep(1)} style={{ padding: '8px 20px', fontSize: '12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={handleExport} style={{ padding: '8px 20px', fontSize: '12px', background: 'none', border: '1px solid #3498db', color: '#3498db', borderRadius: '4px', cursor: 'pointer' }}>📥 Export</button>
          </div>

          {/* ========== ADDED: Comments Section ========== */}
          <div style={{ marginTop: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
            <div 
              style={{ 
                background: '#f5f7fa', 
                padding: '12px 16px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: 600,
                fontSize: '14px',
                color: '#2c3e50'
              }}
              onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}
            >
              <span>💬 Comments & Discussions ({comments.length})</span>
              <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {isCommentsExpanded ? '−' : '+'}
              </span>
            </div>
            
            {isCommentsExpanded && (
              <div style={{ padding: '16px', background: 'white' }}>
                <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '16px' }}>
                  {comments.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                      No comments yet. Be the first to add a comment.
                    </Typography>
                  ) : (
                    comments.map((comment) => (
                      <div
                        key={comment.id}
                        style={{
                          padding: '10px 12px',
                          marginBottom: '8px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          borderLeft: '3px solid #1abc9c'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#2c3e50' }}>
                            {comment.user_email || comment.user_id || 'Unknown'}
                          </span>
                          <span style={{ fontSize: '10px', color: '#95a5a6' }}>
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#34495e', whiteSpace: 'pre-wrap' }}>
                          {comment.text}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment or question..."
                    style={{
                      flex: 1,
                      padding: '10px',
                      fontSize: '13px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      resize: 'vertical',
                      minHeight: '60px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={savingComment || !newComment.trim()}
                    style={{
                      padding: '8px 20px',
                      background: (savingComment || !newComment.trim()) ? '#95a5a6' : '#1abc9c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (savingComment || !newComment.trim()) ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      height: 'fit-content'
                    }}
                  >
                    {savingComment ? 'Sending...' : 'Add Comment'}
                  </button>
                </div>
                
                {!savedAnalysisId && comments.length > 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    💡 Comments will be permanently saved when you click "Save Analysis"
                  </Typography>
                )}
              </div>
            )}
          </div>

          {/* ========== ADDED: Professional Action Area ========== */}
          <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <MuiSelect
                  value={analysisStatus}
                  onChange={(e) => setAnalysisStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="validated">Validated</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </MuiSelect>
              </FormControl>
            </div>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={analysisDescription}
              onChange={(e) => setAnalysisDescription(e.target.value)}
              placeholder="Describe the purpose, methodology, or key findings of this sampling analysis..."
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => setStep(0)}
                disabled={isViewMode}
                size="small"
              >
                New Analysis
              </Button>
              
              <Button
                variant="contained"
                onClick={handleExport}
                size="small"
                sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}
              >
                📥 Export
              </Button>
              
              <Button
                variant="contained"
                onClick={handleSaveAnalysis}
                disabled={!results || isViewMode}
                size="small"
                sx={{ bgcolor: results && !isViewMode ? '#1abc9c' : '#95a5a6', '&:hover': { bgcolor: '#16a085' } }}
              >
                💾 Save Analysis
              </Button>
            </Box>
          </div>
        </div>
      )}
    </DiscretizationLayout>
  );
};
export default SeveritySampling;